import type {
  InvestmentBasicInfoApiData,
  InvestmentConsensusApiData,
  InvestmentCurrencyCode,
  InvestmentFinancialsApiData,
  InvestmentSecurityAttributesApiData,
  InvestmentSignalApiValue,
  InvestmentTechnicalApiData,
  InvestmentTechnicalSignalsApiData,
} from '@app-types/investmentIndicatorsApi';
import type {
  InvestmentIndicatorCardData,
  InvestmentIndicatorsPageData,
  InvestmentInfoBlockData,
  InvestmentInfoTone,
} from '@app-types/investmentIndicators';

const NO_DATA = '--';

const CONSENSUS_RATING_LABELS: Record<string, string> = {
  BUY: '強気',
  OUTPERFORM: 'やや強気',
  HOLD: '中立',
  UNDERPERFORM: 'やや弱気',
  SELL: '弱気',
};

/**
 * Display units derived from a block's own response `currency` (07/09
 * work_items_57). Only monetary rows change; ratios/percentages stay the same.
 */
type InvestmentCurrencyDisplay = {
  currency: 'JPY' | 'USD';
  priceUnit: string;
  millionUnit: string;
  thousandUnit: string;
};

const JPY_DISPLAY: InvestmentCurrencyDisplay = {
  currency: 'JPY',
  priceUnit: '円',
  millionUnit: '百万円',
  thousandUnit: '千円',
};

const USD_DISPLAY: InvestmentCurrencyDisplay = {
  currency: 'USD',
  priceUnit: 'ドル',
  millionUnit: '百万ドル',
  thousandUnit: '千ドル',
};

/**
 * Maps an API currency code to display units. Unknown, blank, or whitespace
 * values keep the existing JPY behavior so confirmed JP rows never blank out.
 */
const getCurrencyDisplay = (
  currency?: InvestmentCurrencyCode | null,
): InvestmentCurrencyDisplay => {
  const normalized = String(currency ?? '').trim().toUpperCase();
  return normalized === 'USD' ? USD_DISPLAY : JPY_DISPLAY;
};

export interface InvestmentIndicatorsApiDataSet {
  basicInfo?: InvestmentBasicInfoApiData | null;
  consensus?: InvestmentConsensusApiData | null;
  securityAttributes?: InvestmentSecurityAttributesApiData | null;
  technical?: InvestmentTechnicalApiData | null;
  financials?: InvestmentFinancialsApiData | null;
  technicalSignals?: InvestmentTechnicalSignalsApiData | null;
}

type DisplayMap = Record<string, string>;
type ToneMap = Record<string, InvestmentInfoTone | undefined>;

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatNumber = (
  value: unknown,
  minimumFractionDigits: number,
  maximumFractionDigits = minimumFractionDigits,
): string | null => {
  const number = toFiniteNumber(value);
  if (number === null) return null;
  return number.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

const withUnit = (
  value: unknown,
  unit: string,
  fractionDigits = 2,
): string => {
  const formatted = formatNumber(value, fractionDigits);
  return formatted === null ? NO_DATA : `${formatted}${unit}`;
};

const formatIntegerWithUnit = (value: unknown, unit = ''): string => {
  const formatted = formatNumber(value, 0, 0);
  return formatted === null ? NO_DATA : `${formatted}${unit}`;
};

const formatConsensusRating = (
  rating: unknown,
  meanLabel: unknown,
): string => {
  const formattedRating = formatNumber(rating, 1);
  if (formattedRating === null) return NO_DATA;

  const normalizedLabel = typeof meanLabel === 'string'
    ? meanLabel.trim().toUpperCase()
    : '';
  const label = CONSENSUS_RATING_LABELS[normalizedLabel];

  // `meanLabel` is the API-owned sentiment source. Never infer a label from
  // the numeric score; preserve the numeric-only display during API rollout.
  return label ? `${formattedRating} (${label})` : formattedRating;
};

const formatFlexibleWithUnit = (value: unknown, unit: string): string => {
  const formatted = formatNumber(value, 0, 2);
  return formatted === null ? NO_DATA : `${formatted}${unit}`;
};

const formatPlain = (value: unknown): string => formatNumber(value, 2) ?? NO_DATA;

const formatText = (value: unknown): string => {
  if (typeof value !== 'string') return NO_DATA;
  const trimmed = value.trim();
  return trimmed || NO_DATA;
};

/**
 * Basic Info indexNames is a comma-delimited API display field. Render each
 * index on its own intentional line; InvestmentInfoTable preserves `\n` via
 * white-space: pre-line. Legacy indexMembership codes are never a fallback.
 */
const formatIndexNames = (value: unknown): string => {
  const text = formatText(value);
  if (text === NO_DATA) return NO_DATA;

  const names = text
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  return names.length > 0 ? names.join('\n') : NO_DATA;
};

const formatSignedNumber = (
  value: unknown,
  fractionDigits: number,
  unit = '',
): string | null => {
  const number = toFiniteNumber(value);
  if (number === null) return null;
  const sign = number > 0 ? '+' : '';
  return `${sign}${formatNumber(number, fractionDigits)}${unit}`;
};

const toneFromNumber = (value: unknown): InvestmentInfoTone | undefined => {
  const number = toFiniteNumber(value);
  if (number === null) return undefined;
  if (number > 0) return 'positive';
  if (number < 0) return 'negative';
  return 'default';
};

const MONTHS: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

const formatTradeDate = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = MONTHS[match[2].toUpperCase()];
  const year = Number(match[3]);
  if (!month || !Number.isInteger(day) || !Number.isInteger(year)) return null;

  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
};

const formatTradeTime = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatPrice = (
  price: unknown,
  tradeDate: unknown,
  tradeTime: unknown,
  unit: string,
): string => {
  const formattedPrice = formatNumber(price, 0, 2);
  if (formattedPrice === null) return NO_DATA;

  const date = formatTradeDate(tradeDate);
  const time = formatTradeTime(tradeTime);
  const timestamp = [date, time].filter(Boolean).join(' ');
  return `${formattedPrice}${unit}${timestamp ? ` (${timestamp})` : ''}`;
};

/**
 * Average turnover is a raw monetary amount; display it in thousands by
 * dividing by 1000 and formatting with 0 fraction digits (07/09 work_items_57).
 * Keeps `0` visible as `0<unit>`; missing/invalid values stay `--`.
 */
const formatTurnoverInThousands = (value: unknown, unit: string): string => {
  const number = toFiniteNumber(value);
  if (number === null) return NO_DATA;
  return `${formatNumber(number / 1000, 0, 0)}${unit}`;
};

const formatPreviousDay = (netChange: unknown, pctChange: unknown): string => {
  const change = toFiniteNumber(netChange);
  const percentage = toFiniteNumber(pctChange);
  const lines: string[] = [];

  if (change !== null) {
    const sign = change > 0 ? '+' : '';
    lines.push(`${sign}${formatNumber(change, 0, 2)}`);
  }
  if (percentage !== null) {
    lines.push(`(${formatSignedNumber(percentage, 2, '%')})`);
  }

  return lines.length ? lines.join('\n') : NO_DATA;
};

const formatSignalTradeDate = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{2})\/(\d{2})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const candidate = new Date(Date.UTC(2000, month - 1, day));
  if (
    month < 1 ||
    month > 12 ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    return null;
  }
  return `${match[1]}日/${match[2]}日`;
};

const formatSignal = (value: InvestmentSignalApiValue | undefined): string => {
  if (value === 1 || value === '1') return '○';
  if (value === 0 || value === '0') return 'X';
  return NO_DATA;
};

const mapBlock = (
  block: InvestmentInfoBlockData,
  values: DisplayMap,
  tones: ToneMap = {},
  labels: DisplayMap = {},
): InvestmentInfoBlockData => ({
  ...block,
  rows: block.rows.map((row) => ({
    ...row,
    label: labels[row.key] ?? row.label,
    value: values[row.key] ?? NO_DATA,
    tone: tones[row.key],
    children: row.children?.map((child) => ({
      ...child,
      label: labels[child.key] ?? child.label,
      value: values[child.key] ?? NO_DATA,
      tone: tones[child.key],
    })),
  })),
});

const mapIndicatorCards = (
  cards: InvestmentIndicatorCardData[],
  basicInfo?: InvestmentBasicInfoApiData | null,
): InvestmentIndicatorCardData[] => {
  // 03/09 contract: company bar label uses the Japanese `nameJp`, not the
  // English `companyName`. Missing/blank `nameJp` renders `--` with no fallback.
  const companyLabel = formatText(basicInfo?.nameJp);
  // Both company and industry card metrics come exclusively from /basic-info.
  // Do not fall back to /financials; that response is only used for the
  // 財務（連）table. Percent values are already percentages (no *100).
  const values = {
    pbr: {
      company: toFiniteNumber(basicInfo?.companyPbr),
      industry: toFiniteNumber(basicInfo?.industry_pbr),
      unit: '倍',
    },
    per: {
      company: toFiniteNumber(basicInfo?.companyPer),
      industry: toFiniteNumber(basicInfo?.industry_per),
      unit: '倍',
    },
    roe: {
      company: toFiniteNumber(basicInfo?.companyRoe),
      industry: toFiniteNumber(basicInfo?.industry_roe),
      unit: '%',
    },
    roa: {
      company: toFiniteNumber(basicInfo?.companyRoa),
      industry: toFiniteNumber(basicInfo?.industry_roa),
      unit: '%',
    },
  };

  return cards.map((card) => {
    const mapped = values[card.key];
    const companyDisplay = withUnit(mapped.company, mapped.unit);
    return {
      ...card,
      valueDisplay: companyDisplay,
      company: {
        label: companyLabel,
        value: mapped.company,
        display: companyDisplay,
      },
      industry: {
        label: '業種平均',
        value: mapped.industry,
        display: withUnit(mapped.industry, mapped.unit),
      },
    };
  });
};

const getBasicValues = (data?: InvestmentBasicInfoApiData | null): DisplayMap => ({
  market: formatText(data?.exchange),
  marketCap: formatIntegerWithUnit(
    data?.marketCap,
    getCurrencyDisplay(data?.currency).millionUnit,
  ),
  tradingUnit: formatIntegerWithUnit(data?.lotSize, '株'),
  // `indexNames` remains the only display source; its comma-delimited members
  // intentionally render one per line to avoid arbitrary browser wrapping.
  indexAdopted: formatIndexNames(data?.indexNames),
  toshoSector: formatText(data?.tse33Sector),
  consensusRating: formatConsensusRating(
    data?.consensusRating,
    data?.meanLabel,
  ),
});

const getBasicLabels = (data?: InvestmentBasicInfoApiData | null): DisplayMap => {
  const units = getCurrencyDisplay(data?.currency);
  return {
    marketCap: `時価総額(${units.millionUnit})`,
  };
};

const getConsensusValues = (data?: InvestmentConsensusApiData | null): DisplayMap => {
  const units = getCurrencyDisplay(data?.currency);
  return {
    'per-forecast': withUnit(data?.per, '倍'),
    'per-change-1y': withUnit(data?.perChgRate1y, '%'),
    'eps-forecast': withUnit(data?.eps, units.priceUnit),
    'peg-forecast': withUnit(data?.peg, '倍'),
    'bps-forecast': withUnit(data?.bvps, units.priceUnit),
    // The current API sample resembles forecast DPS, not dividend yield.
    'dividend-yield-forecast': NO_DATA,
    'dyc-1y': withUnit(data?.divYieldGrowthRate1y, '%'),
    'revenue-growth-5y-past': withUnit(data?.opProfitGrowthRate5y, '%'),
    'revenue-growth-3y-avg': withUnit(data?.revGrowthRate3y, '%'),
    'earnings-revision': withUnit(data?.earnChgRatePrev, '%'),
    'opcr-5d': withUnit(data?.earnChgRate1w, '%'),
    'opcr-20d': withUnit(data?.earnChgRate1m, '%'),
    'opcr-3m': withUnit(data?.earnChgRate3m, '%'),
    'opcr-6m': withUnit(data?.earnChgRate6m, '%'),
    'opcr-1y': withUnit(data?.earnChgRate12m, '%'),
    'debt-to-income': withUnit(data?.intBearNprofitRatio, '%'),
  };
};

const getConsensusLabels = (
  data?: InvestmentConsensusApiData | null,
): DisplayMap => {
  const units = getCurrencyDisplay(data?.currency);
  return {
    'eps-forecast': `EPS(予)(一株あたり当期利益)(${units.priceUnit})`,
    'bps-forecast': `BPS(予)(一株あたり純資産)(${units.priceUnit})`,
  };
};

const getSecurityAttributeValues = (
  data?: InvestmentSecurityAttributesApiData | null,
): DisplayMap => ({
  'margin-buy': formatIntegerWithUnit(data?.buyMargin),
  'margin-sell': formatIntegerWithUnit(data?.sellMargin),
  'wow-buy': formatSignedNumber(data?.buyMarginChg, 0) ?? NO_DATA,
  'wow-sell': formatSignedNumber(data?.sellMarginChg, 0) ?? NO_DATA,
  'margin-ratio': withUnit(data?.marginRatio, '倍'),
});

const getSecurityAttributeTones = (
  data?: InvestmentSecurityAttributesApiData | null,
): ToneMap => ({
  'wow-buy': toneFromNumber(data?.buyMarginChg),
  'wow-sell': toneFromNumber(data?.sellMarginChg),
});

const getTechnicalValues = (
  technical?: InvestmentTechnicalApiData | null,
  basicInfo?: InvestmentBasicInfoApiData | null,
): DisplayMap => {
  const units = getCurrencyDisplay(technical?.currency);
  return {
  'stock-price': formatPrice(
    technical?.price,
    basicInfo?.tradeDate,
    basicInfo?.tradeTime,
    units.priceUnit,
  ),
  'prev-day-diff': formatPreviousDay(technical?.netChange, basicInfo?.pctChange),
  'spcr-5d': withUnit(technical?.prcPct5d, '%'),
  'spcr-20d': withUnit(technical?.prcPct20d, '%'),
  'spcr-3m': withUnit(technical?.prcPct3m, '%'),
  'spcr-6m': withUnit(technical?.prcPct6m, '%'),
  'spcr-1y': withUnit(technical?.prcPct1y, '%'),
  'spcr-3y': withUnit(technical?.prcPct3y, '%'),
  'spcr-5y': withUnit(technical?.prcPct5y, '%'),
  'ytd-low': formatFlexibleWithUnit(technical?.yrLow, units.priceUnit),
  'ytd-low-rise': withUnit(technical?.upRateYrLow, '%'),
  'ytd-high': formatFlexibleWithUnit(technical?.yrHigh, units.priceUnit),
  'ytd-high-drop': withUnit(technical?.downRateYrHigh, '%'),
  'atv-5d': formatTurnoverInThousands(technical?.avgTurnover5, units.thousandUnit),
  'atv-20d': formatTurnoverInThousands(technical?.avgTurnover20, units.thousandUnit),
  'vir-5d': formatPlain(technical?.volChgRate5),
  'vir-20d': formatPlain(technical?.volChgRate20),
  'vir-3m': formatPlain(technical?.volChgRate3m),
  'vir-6m': formatPlain(technical?.volChgRate6m),
  'vir-1y': formatPlain(technical?.volChgRate1y),
  'tvir-5d': formatPlain(technical?.turnoverChgRate5),
  'tvir-20d': formatPlain(technical?.turnoverChgRate20),
  'tvir-3m': formatPlain(technical?.turnoverChgRate3m),
  'tvir-6m': formatPlain(technical?.turnoverChgRate6m),
  'tvir-1y': formatPlain(technical?.turnoverChgRate1y),
  'mad-5d': withUnit(technical?.devSma5, '%'),
  'mad-25d': withUnit(technical?.devSma25, '%'),
  'mad-75d': withUnit(technical?.devSma75, '%'),
  'rsi-14d': formatPlain(technical?.rsi14),
  'psychological-line': formatPlain(technical?.psychoLine),
  'normal-stochastics': formatPlain(technical?.fastSto),
  'slow-stochastic': formatPlain(technical?.slowSto),
  'beta-topix': formatPlain(technical?.betaTopix),
  'beta-nikkei': formatPlain(technical?.betaN225),
  'beta-tse-sector': formatPlain(technical?.betaTse33),
  };
};

const getTechnicalLabels = (
  technical?: InvestmentTechnicalApiData | null,
): DisplayMap => {
  const units = getCurrencyDisplay(technical?.currency);
  return {
    'stock-price': `株価(${units.priceUnit})`,
    'ytd-low': `年初来安値(${units.priceUnit})`,
    'ytd-high': `年初来高値(${units.priceUnit})`,
    'avg-trading-value': `平均売買代金(${units.thousandUnit})`,
  };
};

const getTechnicalTones = (
  technical?: InvestmentTechnicalApiData | null,
  basicInfo?: InvestmentBasicInfoApiData | null,
): ToneMap => ({
  'prev-day-diff': toneFromNumber(
    toFiniteNumber(technical?.netChange) === null
      ? basicInfo?.pctChange
      : technical?.netChange,
  ),
});

const getFinancialValues = (data?: InvestmentFinancialsApiData | null): DisplayMap => {
  const units = getCurrencyDisplay(data?.currency);
  return {
  per: withUnit(data?.per, '倍'),
  'per-cr-1y': withUnit(data?.perChgRate1y, '%'),
  'per-cr-3y': withUnit(data?.perChgRate3y, '%'),
  'per-cr-5y': withUnit(data?.perChgRate5y, '%'),
  eps: withUnit(data?.eps, units.priceUnit),
  pbr: withUnit(data?.pbr, '倍'),
  'pbr-cr-1y': withUnit(data?.pbrChgRate1y, '%'),
  'pbr-cr-3y': withUnit(data?.pbrChgRate3y, '%'),
  'pbr-cr-5y': withUnit(data?.pbrChgRate5y, '%'),
  'div-yield': withUnit(data?.divYield, '%'),
  'dycr-1y': withUnit(data?.divYieldGrowthRate1y, '%'),
  'dycr-3y': withUnit(data?.divYieldGrowthRate3y, '%'),
  'dycr-5y': withUnit(data?.divYieldGrowthRate5y, '%'),
  roe: withUnit(data?.roe, '%'),
  roa: withUnit(data?.roa, '%'),
  revenue: formatIntegerWithUnit(data?.revenue, units.millionUnit),
  'rcr-yoy': withUnit(data?.revGrowthRate1y, '%'),
  psr: withUnit(data?.psr, '倍'),
  'gross-margin': withUnit(data?.revOverseas, '%'),
  'operating-margin': withUnit(data?.opMargin, '%'),
  'ordinary-margin': withUnit(data?.recurringMargin, '%'),
  'net-turnover': withUnit(data?.recRevRatio, '回'),
  'ordinary-profit': formatIntegerWithUnit(data?.ebit, units.millionUnit),
  'opcr-yoy': withUnit(data?.recurringGrowthRate1y, '%'),
  'equity-ratio': withUnit(data?.equityRatio, '%'),
  'prev-total-assets': formatIntegerWithUnit(data?.priorNetProfit, units.millionUnit),
  'debt-equity-ratio': withUnit(data?.intBearDebtRatio, '%'),
  'ev-ebitda': withUnit(data?.evOverEbitda, '倍'),
  'current-ratio': withUnit(data?.currentRatio, '%'),
  'quick-ratio': withUnit(data?.quickRatio, '%'),
  pcfr: withUnit(data?.pcfr, '倍'),
  'inventory-turnover': withUnit(data?.invRevRatio, '回'),
  'margin-volume-ratio': withUnit(data?.marginVolRatio, '倍'),
  'interest-coverage': withUnit(data?.intCovRatio, '倍'),
  };
};

const getFinancialLabels = (
  data?: InvestmentFinancialsApiData | null,
): DisplayMap => {
  const units = getCurrencyDisplay(data?.currency);
  return {
    eps: `EPS(一株あたり当期利益)(${units.priceUnit})`,
    revenue: `売上高(${units.millionUnit})`,
    'ordinary-profit': `経常利益(経営に係る利益)\n(${units.millionUnit})`,
    'prev-total-assets': `前期最終損益(${units.millionUnit})`,
  };
};

const getTechnicalSignalValues = (
  data?: InvestmentTechnicalSignalsApiData | null,
): DisplayMap => ({
  'mid-golden-cross-signal': formatSignal(data?.midGoldenCross),
  'short-golden-cross-signal': formatSignal(data?.shortGoldenCross),
  'mid-dead-cross-signal': formatSignal(data?.midDeadCross),
  'short-dead-cross-signal': formatSignal(data?.shortDeadCross),
  'ichimoku-up': formatSignal(data?.ichimokuCloudAbove),
  'ichimoku-down': formatSignal(data?.ichimokuCloudBelow),
  'dmi-d-buy': formatSignal(data?.dmiBull),
  'dmi-d-sell': formatSignal(data?.dmiBear),
  'dmi-w-buy': formatSignal(data?.dmiBullWeekly),
  'dmi-w-sell': formatSignal(data?.dmiBearWeekly),
  'rci-buy': formatSignal(data?.rciBull),
  'rci-sell': formatSignal(data?.rciBear),
  'macd-d-buy': formatSignal(data?.macdBull),
  'macd-d-sell': formatSignal(data?.macdBear),
  'macd-w-buy': formatSignal(data?.macdBullWeekly),
  'macd-w-sell': formatSignal(data?.macdBearWeekly),
  'ns-buy': formatSignal(data?.fastStoBull),
  'ns-sell': formatSignal(data?.fastStoBear),
  'ss-buy': formatSignal(data?.slowStoBull),
  'ss-sell': formatSignal(data?.slowStoBear),
});

const getTechnicalSignalLabels = (
  data?: InvestmentTechnicalSignalsApiData | null,
): DisplayMap => {
  // One shared formatted trade date is reused across all four cross rows.
  const tradeDate = formatSignalTradeDate(data?.tradeDate) ?? NO_DATA;
  return {
    'mid-golden-cross-signal': tradeDate,
    'short-golden-cross-signal': tradeDate,
    'mid-dead-cross-signal': tradeDate,
    'short-dead-cross-signal': tradeDate,
  };
};

/**
 * Combines the six independently loaded indicator responses into the existing
 * page view model. Every dynamic cell starts at `--`, so loading or a partial
 * endpoint failure can never expose the legacy fixture values.
 */
export const mapInvestmentIndicatorsApiToPageData = (
  api: InvestmentIndicatorsApiDataSet,
  staticTemplate: InvestmentIndicatorsPageData,
): InvestmentIndicatorsPageData => ({
  quickLinks: staticTemplate.quickLinks,
  indicators: mapIndicatorCards(staticTemplate.indicators, api.basicInfo),
  informationColumns: staticTemplate.informationColumns.map((column) => ({
    ...column,
    blocks: column.blocks.map((block) => {
      switch (block.key) {
        case 'basic':
          return mapBlock(
            block,
            getBasicValues(api.basicInfo),
            {},
            getBasicLabels(api.basicInfo),
          );
        case 'consensus':
          return mapBlock(
            block,
            getConsensusValues(api.consensus),
            {},
            getConsensusLabels(api.consensus),
          );
        case 'stockAttributes':
          return mapBlock(
            block,
            getSecurityAttributeValues(api.securityAttributes),
            getSecurityAttributeTones(api.securityAttributes),
          );
        case 'technical':
          return mapBlock(
            block,
            getTechnicalValues(api.technical, api.basicInfo),
            getTechnicalTones(api.technical, api.basicInfo),
            getTechnicalLabels(api.technical),
          );
        case 'finance':
          return mapBlock(
            block,
            getFinancialValues(api.financials),
            {},
            getFinancialLabels(api.financials),
          );
        case 'technicalIndicators':
          return mapBlock(
            block,
            getTechnicalSignalValues(api.technicalSignals),
            {},
            getTechnicalSignalLabels(api.technicalSignals),
          );
        default:
          return mapBlock(block, {});
      }
    }),
  })),
});
